/**
 * The review screen: where a read receipt is checked, its lines put in buckets, and confirmed.
 *
 * PRODUCT.md §10 sets the bar: a ten-line receipt from capture to confirmed in under sixty seconds
 * on a phone. So the common path is taps, not typing. Each line has its buckets as chips, one tap
 * each; "Put the rest in" assigns every line still unassigned at once, because most receipts are
 * one business's; and correction memory has usually named the lines already. Typing is for what
 * was read wrong. A business's line can also be attached to the orders it was bought for
 * (`LineOrders`), here or after confirming.
 *
 * Every line edit saves the whole list (`useSaveLines`), optimistically. While a save that adds or
 * merges lines is out, editing pauses until the server answers with their ids
 * (`hasUnsavedStructure`).
 */

import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { BookCheck, Combine, Pencil, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, FieldError, Input, Notice, Select } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { businessesQuery, categoriesQuery, type Business } from "@/features/settings/queries";
import { cn } from "@/lib/cn";
import { fieldMessage } from "@/lib/fieldMessage";
import { parseMoney } from "@/lib/money";
import { Amount, PurchaseDate } from "./ReceiptBits";
import { ReceiptImage } from "./ReceiptImage";
import { canAttach } from "./allocation";
import { LineOrders } from "./LineOrders";
import {
  receiptAllocationsQuery,
  useConfirmReceipt,
  useSaveLines,
  useUpdateReceipt,
  type Allocation,
  type Receipt,
} from "./queries";
import {
  add,
  addsUp,
  assign,
  assignRest,
  bucketOf,
  edit,
  hasUnsavedStructure,
  linesTotal,
  merge,
  remove,
  setCategory,
  split,
  unassignedCount,
  type Bucket,
  type EditLine,
} from "./review";
import {
  amountInput,
  detailsFromReceipt,
  detailsSchema,
  toDetailsBody,
  type DetailsValues,
} from "./schemas";

export function ReviewScreen({ receipt }: { receipt: Receipt }) {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const save = useSaveLines(receipt.id);
  const allocations = useQuery(receiptAllocationsQuery(receipt.id));
  const [merging, setMerging] = useState<string[] | null>(null);
  const [adding, setAdding] = useState(false);

  const lines = receipt.lines as EditLine[];
  const paused = hasUnsavedStructure(lines);
  const currency = receipt.total?.currency ?? lines[0]?.line_total.currency ?? "CAD";
  const active = (businesses.data ?? []).filter((business) => business.is_active);
  const unassigned = unassignedCount(lines);

  function apply(next: EditLine[]) {
    save.mutate(next);
  }

  return (
    <div className="space-y-5">
      <Details receipt={receipt} currency={currency} />

      {receipt.status === "extraction_failed" && (
        <Notice tone="info">{t("receipts.review.failedEnterBelow")}</Notice>
      )}
      {save.isError && (
        <Notice tone="error">
          {/* The server's reason when it gave one: "now costs less than the shares set for its
              orders" tells the user what to change; "not saved" alone does not. */}
          {save.error instanceof ApiError
            ? t("receipts.review.notSavedBecause", { reason: save.error.message })
            : t("receipts.review.notSaved")}
        </Notice>
      )}

      {unassigned > 0 && lines.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-stone-700">
            {t("receipts.review.assignRest", { count: unassigned })}
          </p>
          <BucketChips
            value={null}
            businesses={active}
            disabled={paused}
            onChange={(bucket) => apply(assignRest(lines, bucket))}
          />
        </div>
      )}

      {lines.length > 1 && (
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-stone-800">{t("receipts.review.lines")}</h2>
          {merging ? (
            <div className="flex gap-3 text-sm">
              <button
                type="button"
                className="text-brand-700 font-medium underline disabled:opacity-50"
                disabled={merging.length < 2 || paused}
                onClick={() => {
                  apply(merge(lines, merging));
                  setMerging(null);
                }}
              >
                {t("receipts.review.mergeSelected", { count: merging.length })}
              </button>
              <button
                type="button"
                className="text-stone-500 underline"
                onClick={() => setMerging(null)}
              >
                {t("common.cancel")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={paused}
              className="text-brand-700 inline-flex items-center gap-1 text-sm underline"
              onClick={() => setMerging([])}
            >
              <Combine className="h-4 w-4" aria-hidden />
              {t("receipts.review.merge")}
            </button>
          )}
        </div>
      )}

      {lines.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {lines.map((line) => (
            <LineRow
              key={line.id}
              receiptId={receipt.id}
              line={line}
              allocations={(allocations.data ?? []).filter(
                (allocation) => allocation.receipt_line_id === line.id,
              )}
              businesses={businesses.data ?? []}
              active={active}
              disabled={paused}
              selecting={merging !== null}
              selected={merging?.includes(line.id) ?? false}
              onSelect={(on) =>
                setMerging((ids) =>
                  ids === null ? ids : on ? [...ids, line.id] : ids.filter((id) => id !== line.id),
                )
              }
              onChange={(next) => apply(next(lines))}
            />
          ))}
        </ul>
      )}

      {adding ? (
        <AddLine
          currency={currency}
          onAdd={(line) => {
            apply(add(lines, { ...line, currency }));
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button type="button" variant="secondary" disabled={paused} onClick={() => setAdding(true)}>
          <span className="inline-flex items-center gap-2">
            <Plus className="h-5 w-5" aria-hidden />
            {t("receipts.review.addLine")}
          </span>
        </Button>
      )}

      <Confirm receipt={receipt} lines={lines} currency={currency} paused={paused} />

      {receipt.has_image && <ReceiptImage receiptId={receipt.id} />}
    </div>
  );
}

// --- the receipt's own details -------------------------------------------------------------------

function Details({ receipt, currency }: { receipt: Receipt; currency: string }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return <DetailsForm receipt={receipt} currency={currency} onDone={() => setEditing(false)} />;
  }
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-stone-600">
          <PurchaseDate date={receipt.purchase_date} />
        </p>
        {receipt.store_address && (
          <p className="truncate text-sm text-stone-500">{receipt.store_address}</p>
        )}
      </div>
      <button
        type="button"
        className="text-brand-700 inline-flex shrink-0 items-center gap-1 text-sm underline"
        onClick={() => setEditing(true)}
      >
        <Pencil className="h-4 w-4" aria-hidden />
        {t("receipts.review.editDetails")}
      </button>
    </div>
  );
}

function DetailsForm({
  receipt,
  currency,
  onDone,
}: {
  receipt: Receipt;
  currency: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateReceipt(receipt.id);
  const form = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema),
    defaultValues: detailsFromReceipt(receipt),
  });
  const taxes = useFieldArray({ control: form.control, name: "tax_lines" });
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync(toDetailsBody(values, currency));
      onDone();
    } catch (error) {
      form.setError("root", {
        type: "server",
        message: error instanceof ApiError ? error.message : t("receipts.errors.saveFailed"),
      });
    }
  });

  return (
    <form
      onSubmit={submit}
      noValidate
      className="space-y-3 rounded-lg border border-stone-200 bg-white p-4"
    >
      <Field
        name="store_name"
        label={t("receipts.manual.store")}
        error={fieldMessage(errors, "store_name", t)}
      >
        <Input id="store_name" {...form.register("store_name")} />
      </Field>
      <Field name="store_address" label={t("receipts.review.address")}>
        <Input id="store_address" {...form.register("store_address")} />
      </Field>
      <Field name="purchase_date" label={t("receipts.manual.date")}>
        <Input id="purchase_date" type="date" {...form.register("purchase_date")} />
      </Field>
      <Field
        name="subtotal"
        label={t("receipts.detail.subtotal")}
        error={fieldMessage(errors, "subtotal", t)}
      >
        <Input id="subtotal" inputMode="decimal" {...form.register("subtotal")} />
      </Field>

      {taxes.fields.map((tax, index) => (
        <div key={tax.id} className="flex items-end gap-2">
          <div className="w-24">
            <Field name={`tax-label-${index}`} label={t("receipts.manual.taxLabel")}>
              <Input id={`tax-label-${index}`} {...form.register(`tax_lines.${index}.label`)} />
            </Field>
          </div>
          <div className="flex-1">
            <Field name={`tax-amount-${index}`} label={t("receipts.manual.taxAmount")}>
              <Input
                id={`tax-amount-${index}`}
                inputMode="decimal"
                {...form.register(`tax_lines.${index}.amount`)}
              />
            </Field>
          </div>
          <button
            type="button"
            className="mb-2 p-2 text-stone-500"
            aria-label={t("receipts.review.removeTax")}
            onClick={() => taxes.remove(index)}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      ))}
      {errors.tax_lines && <FieldError>{t("receipts.errors.taxInvalid")}</FieldError>}
      <button
        type="button"
        className="text-brand-700 text-sm underline"
        onClick={() => taxes.append({ label: "GST", amount: "" })}
      >
        {t("receipts.review.addTax")}
      </button>

      <Field
        name="total"
        label={t("receipts.detail.total")}
        error={fieldMessage(errors, "total", t)}
      >
        <Input id="total" inputMode="decimal" {...form.register("total")} />
      </Field>
      <Field name="notes" label={t("receipts.manual.notes")}>
        <Input id="notes" {...form.register("notes")} />
      </Field>

      {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}
      <div className="flex gap-2">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? t("common.saving") : t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

// --- lines ---------------------------------------------------------------------------------------

/** Household and each active business, one tap each. A select past three, so it still fits. */
function BucketChips({
  value,
  businesses,
  disabled,
  onChange,
}: {
  value: Bucket | null;
  businesses: Business[];
  disabled: boolean;
  onChange: (bucket: Bucket) => void;
}) {
  const { t } = useTranslation();
  const chip = (selected: boolean) =>
    cn(
      "min-h-9 rounded-full border px-3 text-sm font-medium transition-colors disabled:opacity-50",
      selected
        ? "border-brand-700 bg-brand-700 text-white"
        : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50",
    );
  const businessId = value?.type === "business" ? value.businessId : "";

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={disabled}
        aria-pressed={value?.type === "household"}
        className={chip(value?.type === "household")}
        onClick={() => onChange({ type: "household" })}
      >
        {t("settings.categories.household")}
      </button>
      {businesses.length <= 3 ? (
        businesses.map((business) => (
          <button
            key={business.id}
            type="button"
            disabled={disabled}
            aria-pressed={businessId === business.id}
            className={chip(businessId === business.id)}
            onClick={() => onChange({ type: "business", businessId: business.id })}
          >
            {business.name}
          </button>
        ))
      ) : (
        <Select
          value={businessId}
          disabled={disabled}
          aria-label={t("receipts.review.business")}
          className="w-auto min-w-40"
          onChange={(event) =>
            event.target.value && onChange({ type: "business", businessId: event.target.value })
          }
        >
          <option value="">{t("receipts.review.chooseBusiness")}</option>
          {businesses.map((business) => (
            <option key={business.id} value={business.id}>
              {business.name}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}

function CategorySelect({
  bucket,
  value,
  disabled,
  onChange,
}: {
  bucket: Bucket;
  value: string | null;
  disabled: boolean;
  onChange: (categoryId: string | null) => void;
}) {
  const { t } = useTranslation();
  const categories = useQuery(
    categoriesQuery(bucket.type === "business" ? bucket.businessId : null),
  );
  if (!categories.data || categories.data.length === 0) return null;
  return (
    <Select
      value={value ?? ""}
      disabled={disabled}
      aria-label={t("receipts.review.category")}
      className="min-h-9 w-auto max-w-full py-1 text-sm"
      onChange={(event) => onChange(event.target.value || null)}
    >
      <option value="">{t("receipts.review.noCategory")}</option>
      {categories.data.map((category) => (
        <option key={category.id} value={category.id}>
          {category.name}
        </option>
      ))}
    </Select>
  );
}

function LineRow({
  receiptId,
  line,
  allocations,
  businesses,
  active,
  disabled,
  selecting,
  selected,
  onSelect,
  onChange,
}: {
  receiptId: string;
  line: EditLine;
  allocations: Allocation[];
  businesses: Business[];
  active: Business[];
  disabled: boolean;
  selecting: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onChange: (next: (lines: EditLine[]) => EditLine[]) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const bucket = bucketOf(line);
  // A line already in a business since deactivated still shows it, so the choice is not lost.
  const assigned = businesses.find((business) => business.id === line.business_id);
  const choices = assigned && !assigned.is_active ? [...active, assigned] : active;
  const name = line.description || line.raw_text || t("receipts.review.unnamed");

  if (editing) {
    return (
      <li className="px-4 py-3">
        <LineEditor
          line={line}
          onSave={(changes) => {
            onChange((lines) => edit(lines, line.id, changes));
            setEditing(false);
          }}
          onSplit={(amountMinor) => {
            onChange((lines) => split(lines, line.id, amountMinor));
            setEditing(false);
          }}
          onRemove={() => {
            onChange((lines) => remove(lines, line.id));
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li className={cn("space-y-2 px-4 py-3", selected && "bg-brand-50")}>
      <div className="flex items-start gap-3">
        {selecting && (
          <input
            type="checkbox"
            className="mt-1 h-5 w-5"
            checked={selected}
            aria-label={t("receipts.review.selectLine", { name })}
            onChange={(event) => onSelect(event.target.checked)}
          />
        )}
        <button
          type="button"
          disabled={disabled || selecting}
          className="min-w-0 flex-1 text-left"
          onClick={() => setEditing(true)}
        >
          <span className="flex items-center gap-1.5 text-stone-800">
            {line.product_id && (
              <BookCheck
                className="text-brand-700 h-4 w-4 shrink-0"
                aria-label={t("receipts.review.remembered")}
              />
            )}
            {name}
          </span>
          {line.raw_text && line.raw_text !== line.description && (
            <span className="block truncate text-xs text-stone-500">{line.raw_text}</span>
          )}
        </button>
        <Amount money={line.line_total} className="text-stone-800" />
      </div>

      {!selecting && (
        // One row where it fits: a category picker on its own row under every line made a
        // ten-line receipt a long scroll on a phone.
        <div className="flex flex-wrap items-center gap-2">
          <BucketChips
            value={bucket}
            businesses={choices}
            disabled={disabled}
            onChange={(next) => onChange((lines) => assign(lines, [line.id], next))}
          />
          {bucket && (
            <CategorySelect
              bucket={bucket}
              value={line.category_id}
              disabled={disabled}
              onChange={(categoryId) =>
                onChange((lines) => setCategory(lines, line.id, categoryId))
              }
            />
          )}
        </div>
      )}
      {!selecting && canAttach(line) && (
        <LineOrders
          receiptId={receiptId}
          line={line}
          allocations={allocations}
          disabled={disabled}
        />
      )}
    </li>
  );
}

function LineEditor({
  line,
  onSave,
  onSplit,
  onRemove,
  onCancel,
}: {
  line: EditLine;
  onSave: (changes: { description: string; amountMinor: number }) => void;
  onSplit: (amountMinor: number) => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const currency = line.line_total.currency;
  const [description, setDescription] = useState(line.description);
  const [amount, setAmount] = useState(amountInput(line.line_total));
  const [splitting, setSplitting] = useState(false);
  const [part, setPart] = useState("");
  const parsed = parseMoney(amount, currency);
  const parsedPart = parseMoney(part, currency);

  return (
    <div className="space-y-3">
      {line.raw_text && <p className="text-xs text-stone-500">{line.raw_text}</p>}
      <Field name={`description-${line.id}`} label={t("receipts.manual.lineDescription")}>
        <Input
          id={`description-${line.id}`}
          value={description}
          autoFocus
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field
        name={`amount-${line.id}`}
        label={t("receipts.manual.lineAmount")}
        error={parsed ? undefined : t("receipts.errors.amountInvalid")}
      >
        <Input
          id={`amount-${line.id}`}
          inputMode="decimal"
          value={amount}
          invalid={!parsed}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>

      {splitting ? (
        <div className="space-y-2 rounded-lg bg-stone-50 p-3">
          <Field
            name={`split-${line.id}`}
            label={t("receipts.review.splitAmount")}
            error={part && !parsedPart ? t("receipts.errors.amountInvalid") : undefined}
          >
            <Input
              id={`split-${line.id}`}
              inputMode="decimal"
              value={part}
              autoFocus
              onChange={(event) => setPart(event.target.value)}
            />
          </Field>
          <Button
            type="button"
            disabled={!parsedPart || parsedPart.minor === 0}
            onClick={() => parsedPart && onSplit(parsedPart.minor)}
          >
            {t("receipts.review.split")}
          </Button>
        </div>
      ) : (
        <div className="flex gap-4 text-sm">
          <button
            type="button"
            className="text-brand-700 underline"
            onClick={() => setSplitting(true)}
          >
            {t("receipts.review.split")}
          </button>
          <button type="button" className="text-red-700 underline" onClick={onRemove}>
            {t("receipts.review.removeLine")}
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          disabled={!parsed}
          onClick={() =>
            parsed && onSave({ description: description.trim(), amountMinor: parsed.minor })
          }
        >
          {t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}

function AddLine({
  currency,
  onAdd,
  onCancel,
}: {
  currency: string;
  onAdd: (line: { description: string; amountMinor: number }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const parsed = parseMoney(amount, currency);

  return (
    <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-4">
      <Field name="new-description" label={t("receipts.manual.lineDescription")}>
        <Input
          id="new-description"
          value={description}
          autoFocus
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field name="new-amount" label={t("receipts.manual.lineAmount")}>
        <Input
          id="new-amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      <div className="flex gap-2">
        <Button
          type="button"
          disabled={!parsed || !description.trim()}
          onClick={() =>
            parsed && onAdd({ description: description.trim(), amountMinor: parsed.minor })
          }
        >
          {t("receipts.review.addLine")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}

// --- totals and confirm --------------------------------------------------------------------------

function Confirm({
  receipt,
  lines,
  currency,
  paused,
}: {
  receipt: Receipt;
  lines: EditLine[];
  currency: string;
  paused: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const confirm = useConfirmReceipt(receipt.id);
  const [error, setError] = useState<string | undefined>();

  const taxes = receipt.tax_lines.map((tax) => tax.amount);
  const agrees = addsUp(lines, taxes, receipt.subtotal, receipt.total);
  const unassigned = unassignedCount(lines);
  const keepsImages = me.data?.workspace?.keeps_receipt_images ?? false;
  const blocked = lines.length === 0 || unassigned > 0 || paused || confirm.isPending;

  async function go() {
    setError(undefined);
    try {
      await confirm.mutateAsync(!agrees);
      await navigate({ to: "/receipts" });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("receipts.errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-3">
      <dl className="space-y-1.5 rounded-lg border border-stone-200 bg-white px-4 py-3">
        <Row label={t("receipts.review.linesTotal")} money={linesTotal(lines, currency)} />
        {receipt.tax_lines.map((tax) => (
          <Row key={tax.id} label={tax.label} money={tax.amount} />
        ))}
        <Row label={t("receipts.detail.total")} money={receipt.total} strong />
      </dl>

      {!agrees && <Notice tone="info">{t("receipts.review.mismatch")}</Notice>}
      {lines.length === 0 && <Notice tone="info">{t("receipts.review.noLines")}</Notice>}
      {unassigned > 0 && lines.length > 0 && (
        <p className="text-sm text-stone-600">
          {t("receipts.review.unassigned", { count: unassigned })}
        </p>
      )}
      {!keepsImages && (
        <p className="text-sm text-stone-600">
          {receipt.has_image
            ? t("receipts.review.keepPaperDeleted")
            : t("receipts.review.keepPaper")}
        </p>
      )}
      {error && <Notice tone="error">{error}</Notice>}

      <Button type="button" disabled={blocked} onClick={() => void go()}>
        {confirm.isPending
          ? t("common.saving")
          : agrees
            ? t("receipts.review.confirm")
            : t("receipts.review.confirmAnyway")}
      </Button>
    </div>
  );
}

function Row({
  label,
  money,
  strong = false,
}: {
  label: string;
  money: Receipt["total"];
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className={strong ? "font-semibold text-stone-900" : "text-stone-600"}>{label}</dt>
      <dd>
        <Amount money={money} className={strong ? "font-semibold text-stone-900" : ""} />
      </dd>
    </div>
  );
}
